import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ModelViewer from '../components/ModelViewer';
import { useAuthStore } from '../store/authStore';
import { useModelStore } from '../store/modelStore';
import { apiUrl } from '../lib/api';
import type { Model } from '../types';

export default function ModelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const removeModel = useModelStore((s) => s.removeModel);
  const [model, setModel] = useState<Model | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    import('../lib/api').then((m) => m.apiFetch(`/api/models/${id}`))
      .then((res) => res.json())
      .then((data) => {
        const payload = data as { model?: Model };
        setModel(payload.model ?? null);
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied');
  };

  const handleDelete = async () => {
    if (!model) return;
    if (!window.confirm(`Delete "${model.name}"? This cannot be undone.`)) return;
    setIsDeleting(true);
    try {
      const { apiFetch } = await import('../lib/api');
      const res = await apiFetch(`/api/models/${model.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = (await res.json()) as { error?: string };
        throw new Error(data.error || 'Delete failed');
      }
      removeModel(model.id);
      navigate('/library');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
      setIsDeleting(false);
    }
  };

  if (isLoading) return <div className="container"><p>Loading...</p></div>;
  if (!model) return <div className="container"><p>Model not found</p></div>;

  const isOwner = Boolean(user && user.id === model.ownerId);
  const assetUrl = apiUrl(model.modelUrl);

  return (
    <div className="container" style={{ paddingTop: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>{model.name}</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={copyLink}>Copy Link</button>
          <a href={assetUrl} download>
            <button style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid #475569' }}>
              Download
            </button>
          </a>
          {isOwner && (
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              style={{ background: 'var(--danger)', color: '#fff' }}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          )}
        </div>
      </div>
      <p style={{ color: 'var(--muted)' }}>{model.description || 'No description'}</p>
      <div style={{ height: '60vh', borderRadius: '0.75rem', overflow: 'hidden' }}>
        <ModelViewer url={assetUrl} />
      </div>
    </div>
  );
}
